import React, { useState, useEffect } from 'react';
import { Modal, Button, FormControl, DropdownButton, MenuItem, Alert } from 'react-bootstrap';
import { getUser } from '../../utils/Common';
import { postShareWithEmail, shareCapWithUser, checkUserExists } from '../../utils/bedrockAPI';

export default function AddUserModal({ 
  show, 
  onHide, 
  caps, 
  userCaps, 
  currentUser, 
  isMobile, 
  getAccessColor, 
  getReadableAccessLevel,
  onUsersAdded 
}) {
  const [emailInput, setEmailInput] = useState('');
  const [emailList, setEmailList] = useState([]);
  const [newUserAccess, setNewUserAccess] = useState({});
  const [powerNotifications, setPowerNotifications] = useState({});
  const [userExistsErrors, setUserExistsErrors] = useState({});
  const [isEmployee, setIsEmployee] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [inputError, setInputError] = useState(null);

  // Validation functions
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const hasCameraAccess = () => {
    return Object.values(newUserAccess).some(accessType => accessType !== 'No Access');
  };

  const isFormValid = () => {
    return emailList.length > 0 && hasCameraAccess();
  };

  // Event handlers
  const handleEmailInputChange = (e) => {
    setEmailInput(e.target.value);
    setInputError(null);
  };

  const handleEmailInputKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail();
    }
  };

  const addEmail = async () => {
    const email = emailInput.trim().toLowerCase();
    if (email && isValidEmail(email) && !emailList.includes(email)) {
      const userExists = await checkUserExists(email);
      if (!userExists.exists) {
        setEmailList([...emailList, email]);
        setEmailInput('');
        setInputError(null);
      } else {
        setInputError('User already exists');
      }
    }
  };
  
  const removeEmail = (emailToRemove) => {
    setEmailList(emailList.filter(email => email !== emailToRemove));
    setUserExistsErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[emailToRemove];
      return newErrors;
    });
  };

  const handleNewUserAccessChange = (capId, accessType) => {
    setNewUserAccess(prev => ({
      ...prev,
      [capId]: accessType
    }));
  };

  const handlePowerNotificationChange = (capId, checked) => {
    setPowerNotifications(prev => ({
      ...prev,
      [capId]: checked
    }));
  };

  const handleAddUsers = async () => {
    if (!isFormValid()) return;

    setIsProcessing(true);
    const accessChanges = Object.entries(newUserAccess).map(([capId, accessType]) => ({
      capId,
      accessType,
      powerNotifications: powerNotifications[capId] || false
    }));

    const results = {
      success: [],
      failed: []
    };

    for (const email of emailList) {
      try {
        const userExists = await checkUserExists(email);
        if (userExists.exists) {
          setUserExistsErrors(prev => ({ ...prev, [email]: true }));
          results.failed.push(email);
          continue;
        }

        let newUser;
        if (accessChanges.length > 0) {
          newUser = await postShareWithEmail(
            accessChanges[0].capId, 
            email, 
            accessChanges[0].accessType, 
            accessChanges[0].powerNotifications, 
            isEmployee
          );
        }

        if (accessChanges.length > 1 && newUser) {
          for (let i = 1; i < accessChanges.length; i++) {
            await shareCapWithUser(
              accessChanges[i].capId, 
              newUser.userId, 
              accessChanges[i].accessType, 
              accessChanges[i].powerNotifications
            );
          }
        }
        results.success.push(email);
      } catch (error) {
        console.error(`Error adding user ${email}:`, error);
        results.failed.push(email);
      }
    }

    // Reset form
    setEmailInput('');
    setEmailList([]);
    setNewUserAccess({});
    setPowerNotifications({});
    setUserExistsErrors({});
    setIsEmployee(false);
    setIsProcessing(false);
    onHide();
    
    // Call the callback to refresh the users list
    if (results.success.length > 0 && onUsersAdded) {
      onUsersAdded();
    }
  };

  // Render functions
  const renderEmailInput = () => (
    <div className="email-input-section">
      <label>Email Addresses</label>
      <div className="email-input-container">
        <FormControl
          type="text"
          placeholder="Enter email addresses (press Enter or comma to add)"
          value={emailInput}
          onChange={handleEmailInputChange}
          onKeyDown={handleEmailInputKeyPress}
          className={`add-user-input ${inputError ? 'has-error' : ''}`}
          disabled={isProcessing}
        />
        <Button 
          bsStyle="primary" 
          onClick={addEmail}
          disabled={!isValidEmail(emailInput) || isProcessing}
          className="add-email-button"
        >
          Add
        </Button>
      </div>
      {emailInput && !isValidEmail(emailInput) && (
        <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
          Please enter a valid email address
        </div>
      )}
      {inputError && (
        <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
          {inputError}
        </div>
      )}
      {emailList.length > 0 && (
        <div className="email-list">
          {emailList.map(email => (
            <div key={email} className={`email-tag ${userExistsErrors[email] ? 'has-error' : ''}`}>
              <span>{email}</span>
              <button 
                type="button" 
                onClick={() => removeEmail(email)}
                disabled={isProcessing}
                className="remove-email-button"
              >
                ×
              </button>
              {userExistsErrors[email] && (
                <span className="email-error">User already exists</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderCameraAccess = () => (
    <>
      <div className="access-controls-section">
        <div className="access-controls-header">
          <div className="header-column camera-column">Camera</div>
          <div className="header-column access-column">Access Level</div>
          <div className="header-column notification-column">Power Notifications</div>
        </div>
        {caps.map(cap => {
          const currentUserCap = userCaps.find(
            uc => uc.userId === currentUser.userId && uc.capId === cap.capId
          );
          const hasAccess = currentUserCap && currentUserCap.accessType === 'OWNER';
          const accessLevel = newUserAccess[cap.capId] || 'No Access';
          const hasPowerNotifications = powerNotifications[cap.capId] || false;
          
          return (
            <div key={cap.capId} className="access-control-row">
              <div className="row-column camera-column">
                <span className="camera-name">{cap.name}</span>
              </div>
              <div className="row-column access-column">
                <DropdownButton
                  title={getReadableAccessLevel(accessLevel, null, isMobile)}
                  id={`modal-dropdown-${cap.capId}`}
                  onSelect={(newType) => handleNewUserAccessChange(cap.capId, newType)}
                  style={{backgroundColor: getAccessColor(accessLevel, null)}}
                  disabled={!hasAccess}
                  bsSize="small"
                >
                  <MenuItem eventKey="No Access" style={{ backgroundColor: getAccessColor('No Access', null, isMobile)}}>
                    {getReadableAccessLevel('No Access', null, isMobile)}
                  </MenuItem>
                  <MenuItem eventKey="DEMO" style={{ backgroundColor: getAccessColor('DEMO', null, isMobile)}}>
                    {getReadableAccessLevel('DEMO', null, isMobile)}
                  </MenuItem>
                  <MenuItem eventKey="VIEWER" style={{ backgroundColor: getAccessColor('VIEWER', null, isMobile)}}>
                    {getReadableAccessLevel('VIEWER', null, isMobile)}
                  </MenuItem>
                  <MenuItem eventKey="OWNER" style={{ backgroundColor: getAccessColor('OWNER', null, isMobile)}}>
                    {getReadableAccessLevel('OWNER', null, isMobile)}
                  </MenuItem>
                  {getUser().role==='ADMIN' && (
                    <MenuItem eventKey="SALES_DEMO" style={{ backgroundColor: getAccessColor('SALES_DEMO', null, isMobile)}}>
                      {getReadableAccessLevel('SALES_DEMO', null, isMobile)}
                    </MenuItem>
                  )}
                </DropdownButton>
              </div>
              <div className="row-column notification-column">
                <div className="power-notification-toggle">
                  <input
                    type="checkbox"
                    checked={hasPowerNotifications}
                    onChange={(e) => handlePowerNotificationChange(cap.capId, e.target.checked)}
                    disabled={!hasAccess || accessLevel === 'No Access'}
                    id={`power-notification-${cap.capId}`}
                  />
                  <label htmlFor={`power-notification-${cap.capId}`}>
                    Email
                  </label>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {!hasCameraAccess() && (
        <div className="text-danger">
          Please grant access to at least one camera
        </div>
      )}
    </>
  );

  return (
    <Modal show={show} onHide={onHide} dialogClassName="add-user-modal">
      <Modal.Header closeButton>
        <Modal.Title>Add New Users</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="add-user-modal-content">
          {renderEmailInput()}
          {renderCameraAccess()}
          <div className="employee-checkbox-section">
            <div className="power-notification-toggle">
              <input
                type="checkbox"
                checked={isEmployee}
                onChange={(e) => setIsEmployee(e.target.checked)}
                id="employee-checkbox"
                disabled={isProcessing}
              />
              <label htmlFor="employee-checkbox">
                Add as employees
              </label>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onHide} disabled={isProcessing}>Cancel</Button>
        <Button 
          bsStyle="success" 
          onClick={handleAddUsers}
          disabled={!isFormValid() || isProcessing}
        >
          {isProcessing ? 'Adding Users...' : 'Add Users'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
} 